/**
 * Project Store - Zustand
 *
 * Manages project state including:
 * - Current project
 * - Projects list
 * - Project operations (create, save, load, delete)
 * - Auto-save functionality
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Project, ProjectCreateInput, ProjectUpdateInput } from '@canvas/shared-types';

export interface ProjectStore {
  // State
  currentProject: Project | null;
  projects: Project[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastSaved: Date | null;
  autoSaveEnabled: boolean;
  autoSaveInterval: number; // in milliseconds

  // Actions
  setCurrentProject: (project: Project) => void;
  setProjects: (projects: Project[]) => void;
  addProject: (project: Project) => void;
  updateProject: (project: Project) => void;
  removeProject: (projectId: string) => void;
  clearCurrentProject: () => void;

  // Operations
  createProject: (input: ProjectCreateInput, onSave: (project: Project) => Promise<void>) => Promise<Project | null>;
  loadProject: (projectId: string, onLoad: (projectId: string) => Promise<Project>) => Promise<Project | null>;
  saveProject: (updates?: ProjectUpdateInput, onSave?: (project: Project) => Promise<void>) => Promise<boolean>;
  deleteProject: (projectId: string, onDelete?: (projectId: string) => Promise<void>) => Promise<boolean>;

  // Auto-save
  enableAutoSave: (intervalMs?: number) => void;
  disableAutoSave: () => void;
  autoSave: (onSave?: (project: Project) => Promise<void>) => Promise<void>;

  // Utilities
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
}

/**
 * Project Store
 * Manages all project-related state and operations
 */
export const useProjectStore = create<ProjectStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        currentProject: null,
        projects: [],
        isLoading: false,
        isSaving: false,
        error: null,
        lastSaved: null,
        autoSaveEnabled: true,
        autoSaveInterval: 10000, // 10 seconds default

        /**
         * Set the currently active project
         */
        setCurrentProject: (project: Project) => {
          set({ currentProject: project, error: null });
        },

        /**
         * Set the entire projects list
         * Used when loading projects from Firestore
         */
        setProjects: (projects: Project[]) => {
          set({ projects, error: null });
        },

        /**
         * Add a single project to the list
         */
        addProject: (project: Project) => {
          set((state) => ({
            projects: [project, ...state.projects],
            error: null,
          }));
        },

        /**
         * Update a project in the list
         */
        updateProject: (project: Project) => {
          set((state) => ({
            projects: state.projects.map((p) =>
              p.projectId === project.projectId ? project : p
            ),
            // If this is the current project, update it too
            currentProject:
              state.currentProject?.projectId === project.projectId
                ? project
                : state.currentProject,
            lastSaved: new Date(),
            error: null,
          }));
        },

        /**
         * Remove a project from the list
         */
        removeProject: (projectId: string) => {
          set((state) => ({
            projects: state.projects.filter((p) => p.projectId !== projectId),
            // If this was the current project, clear it
            currentProject:
              state.currentProject?.projectId === projectId
                ? null
                : state.currentProject,
          }));
        },

        /**
         * Clear the currently active project
         */
        clearCurrentProject: () => {
          set({ currentProject: null });
        },

        /**
         * Create a new project
         *
         * @param input - Project creation input
         * @param onSave - Callback to save to Firestore
         */
        createProject: async (
          input: ProjectCreateInput,
          onSave: (project: Project) => Promise<void>
        ): Promise<Project | null> => {
          set({ isLoading: true, error: null });

          try {
            const now = new Date();
            const tempId = `proj-${Date.now()}`;

            // Create project object with temporary ID
            // Server will assign actual ID
            const newProject: Project = {
              projectId: tempId,
              userId: '', // Will be set by server
              name: input.name,
              description: input.description || '',
              canvasData: input.canvasData || {
                version: '5.3.0',
                objects: [],
                width: input.width || 1200,
                height: input.height || 800,
              },
              width: input.width || 1200,
              height: input.height || 800,
              status: 'draft',
              isPublished: false,
              collaborators: [],
              versionCount: 1,
              fileSize: 0,
              viewCount: 0,
              forkCount: 0,
              tags: input.tags || [],
              commentsEnabled: true,
              usesAI: false,
              showAuthorOnPublished: true,
              createdAt: now,
              lastModifiedAt: now,
              lastModifiedBy: '', // Will be set by server
              updatedAt: now,
            };

            // Save to Firestore
            await onSave(newProject);

            // Add to local list
            set((state) => ({
              projects: [newProject, ...state.projects],
              currentProject: newProject,
              isLoading: false,
              lastSaved: new Date(),
              error: null,
            }));

            return newProject;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create project';
            set({ isLoading: false, error: message });
            return null;
          }
        },

        /**
         * Load a project from Firestore
         *
         * @param projectId - ID of project to load
         * @param onLoad - Callback to load from Firestore
         */
        loadProject: async (
          projectId: string,
          onLoad: (projectId: string) => Promise<Project>
        ): Promise<Project | null> => {
          set({ isLoading: true, error: null });

          try {
            const project = await onLoad(projectId);

            set({
              currentProject: project,
              isLoading: false,
              lastSaved: new Date(),
              error: null,
            });

            return project;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to load project';
            set({ isLoading: false, error: message });
            return null;
          }
        },

        /**
         * Save current project
         * Updates canvas data and metadata
         *
         * @param updates - Partial updates to apply
         * @param onSave - Callback to save to Firestore
         */
        saveProject: async (
          updates?: ProjectUpdateInput,
          onSave?: (project: Project) => Promise<void>
        ): Promise<boolean> => {
          const state = get();
          const { currentProject } = state;

          if (!currentProject) {
            set({ error: 'No project currently loaded' });
            return false;
          }

          if (!onSave) {
            set({ error: 'No save callback provided' });
            return false;
          }

          set({ isSaving: true, error: null });

          try {
            // Merge updates with current project
            const updatedProject: Project = {
              ...currentProject,
              ...updates,
              projectId: currentProject.projectId,
              userId: currentProject.userId,
              createdAt: currentProject.createdAt,
              lastModifiedAt: new Date(),
              updatedAt: new Date(),
            };

            // Save to Firestore
            await onSave(updatedProject);

            // Update local state
            set({
              currentProject: updatedProject,
              isSaving: false,
              lastSaved: new Date(),
              error: null,
            });

            // Also update in projects list
            get().updateProject(updatedProject);

            return true;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to save project';
            set({ isSaving: false, error: message });
            return false;
          }
        },

        /**
         * Delete a project
         *
         * @param projectId - ID of project to delete
         * @param onDelete - Callback to delete from Firestore
         */
        deleteProject: async (
          projectId: string,
          onDelete?: (projectId: string) => Promise<void>
        ): Promise<boolean> => {
          if (!onDelete) {
            set({ error: 'No delete callback provided' });
            return false;
          }

          set({ isLoading: true, error: null });

          try {
            // Delete from Firestore
            await onDelete(projectId);

            // Remove from local state
            get().removeProject(projectId);

            set({ isLoading: false });
            return true;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to delete project';
            set({ isLoading: false, error: message });
            return false;
          }
        },

        /**
         * Enable auto-save
         * Automatically saves current project at specified interval
         */
        enableAutoSave: (intervalMs = 10000) => {
          set({ autoSaveEnabled: true, autoSaveInterval: intervalMs });
        },

        /**
         * Disable auto-save
         */
        disableAutoSave: () => {
          set({ autoSaveEnabled: false });
        },

        /**
         * Perform auto-save
         * Called by useEffect or timer
         */
        autoSave: async (onSave?: (project: Project) => Promise<void>) => {
          const state = get();
          const { autoSaveEnabled, currentProject, isSaving } = state;

          // Don't auto-save if:
          // - Auto-save is disabled
          // - Currently saving
          // - No project loaded
          if (!autoSaveEnabled || isSaving || !currentProject) {
            return;
          }

          // Perform save
          await get().saveProject(undefined, onSave);
        },

        /**
         * Set error message
         */
        setError: (error: string | null) => {
          set({ error });
        },

        /**
         * Set loading state
         */
        setLoading: (loading: boolean) => {
          set({ isLoading: loading });
        },

        /**
         * Set saving state
         */
        setSaving: (saving: boolean) => {
          set({ isSaving: saving });
        },
      }),
      {
        name: 'project-store',
        partialize: (state) => ({
          projects: state.projects,
          autoSaveEnabled: state.autoSaveEnabled,
          autoSaveInterval: state.autoSaveInterval,
          lastSaved: state.lastSaved,
        }),
      }
    ),
    { name: 'project-store' }
  )
);
